/**
 * Web stand-in for `react-native-vision-camera`.
 *
 * On Android the sell screen opens VisionCamera to scan a barcode with the
 * phone's camera. On a desktop POS the primary scanner is a USB/Bluetooth
 * keyboard-wedge device (see src/platform/scanner), so this component is a
 * fallback rather than the main path.
 *
 * It renders a live getUserMedia preview and, when the browser supports the
 * BarcodeDetector API (Chrome/Edge on Windows do), reports detected codes
 * through the same `codeScanner` contract VisionCamera uses. Where
 * BarcodeDetector is unavailable the preview still renders and an inline
 * notice points the operator at the keyboard-wedge scanner.
 */

import React, {useCallback, useEffect, useRef, useState} from 'react';
import {StyleSheet, Text, View} from 'react-native';

export type Code = {
  type?: string;
  value?: string;
  frame?: {x: number; y: number; width: number; height: number};
};

export type CameraDevice = {
  id: string;
  name: string;
  position: 'back' | 'front' | 'external';
};

export type CodeScanner = {
  codeTypes: string[];
  onCodeScanned: (codes: Code[]) => void;
};

export type CameraProps = {
  device?: CameraDevice | null;
  isActive?: boolean;
  codeScanner?: CodeScanner;
  style?: any;
  enableZoomGesture?: boolean;
  resizeMode?: 'cover' | 'contain';
  [key: string]: any;
};

/** VisionCamera's code type names -> BarcodeDetector format names. */
const CODE_TYPE_MAP: Record<string, string> = {
  'qr': 'qr_code',
  'ean-13': 'ean_13',
  'ean-8': 'ean_8',
  'code-128': 'code_128',
  'code-39': 'code_39',
  'code-93': 'code_93',
  'upc-a': 'upc_a',
  'upc-e': 'upc_e',
  'data-matrix': 'data_matrix',
  'pdf-417': 'pdf417',
  'aztec': 'aztec',
  'itf': 'itf',
  'codabar': 'codabar',
};

export function useCameraDevice(position: 'back' | 'front' = 'back'): CameraDevice | null {
  const [device, setDevice] = useState<CameraDevice | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (typeof navigator === 'undefined' || !navigator.mediaDevices) {
      return undefined;
    }
    navigator.mediaDevices
      .enumerateDevices()
      .then(devices => {
        if (cancelled) {
          return;
        }
        const cameras = devices.filter(d => d.kind === 'videoinput');
        if (cameras.length > 0) {
          setDevice({
            id: cameras[0].deviceId,
            name: cameras[0].label || 'Camera',
            position,
          });
        }
      })
      .catch(() => {
        /* no camera access - device stays null and <Camera> is not rendered */
      });
    return () => {
      cancelled = true;
    };
  }, [position]);

  return device;
}

export function useCodeScanner(scanner: CodeScanner): CodeScanner {
  return scanner;
}

export function useCameraPermission() {
  return {
    hasPermission: true,
    requestPermission: async () => true,
  };
}

export const Camera = (props: CameraProps) => {
  const {device, isActive = true, codeScanner, style} = props;
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [detectorMissing, setDetectorMissing] = useState(false);

  const attachVideo = useCallback((node: HTMLVideoElement | null) => {
    videoRef.current = node;
    if (node && streamRef.current) {
      node.srcObject = streamRef.current;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    if (!isActive || !device) {
      return undefined;
    }
    if (typeof navigator === 'undefined' || !navigator.mediaDevices) {
      setError('This browser does not provide camera access.');
      return undefined;
    }

    navigator.mediaDevices
      .getUserMedia({video: {deviceId: device.id ? {exact: device.id} : undefined}, audio: false})
      .then(stream => {
        if (cancelled) {
          stream.getTracks().forEach(track => track.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      })
      .catch(err => {
        if (!cancelled) {
          setError(err?.message || 'Unable to start the camera.');
        }
      });

    return () => {
      cancelled = true;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
    };
  }, [device, isActive]);

  // Barcode detection loop.
  useEffect(() => {
    if (!isActive || !codeScanner) {
      return undefined;
    }
    const DetectorCtor = (window as any).BarcodeDetector;
    if (!DetectorCtor) {
      setDetectorMissing(true);
      return undefined;
    }

    const formats = codeScanner.codeTypes
      .map(type => CODE_TYPE_MAP[type])
      .filter(Boolean) as string[];

    let detector: any;
    try {
      detector = new DetectorCtor(formats.length ? {formats} : undefined);
    } catch {
      setDetectorMissing(true);
      return undefined;
    }

    let stopped = false;
    const tick = async () => {
      if (stopped) {
        return;
      }
      const video = videoRef.current;
      if (video && video.readyState >= 2) {
        try {
          const found = await detector.detect(video);
          if (found && found.length > 0 && !stopped) {
            codeScanner.onCodeScanned(
              found.map((item: any) => ({
                type: item.format,
                value: item.rawValue,
                frame: item.boundingBox,
              })),
            );
          }
        } catch {
          /* transient decode failures are expected between frames */
        }
      }
      if (!stopped) {
        window.setTimeout(tick, 250);
      }
    };
    tick();

    return () => {
      stopped = true;
    };
  }, [codeScanner, isActive]);

  return (
    <View style={[styles.container, style]}>
      {React.createElement('video', {
        ref: attachVideo,
        autoPlay: true,
        muted: true,
        playsInline: true,
        style: {width: '100%', height: '100%', objectFit: props.resizeMode || 'cover'},
      })}
      {error ? (
        <View style={styles.notice}>
          <Text style={styles.noticeText}>{error}</Text>
        </View>
      ) : null}
      {detectorMissing && !error ? (
        <View style={styles.notice}>
          <Text style={styles.noticeText}>
            This browser cannot decode barcodes from the camera. Use the USB/Bluetooth scanner or
            type the code instead.
          </Text>
        </View>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: '#000000', overflow: 'hidden'},
  notice: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    padding: 12,
    backgroundColor: 'rgba(0,0,0,0.65)',
  },
  noticeText: {color: '#ffffff', fontSize: 13, textAlign: 'center'},
});

export default {Camera, useCameraDevice, useCodeScanner, useCameraPermission};
